import React, { useState } from "react";
import { Empty, Skeleton } from "antd";
import { CaretRightOutlined } from "@ant-design/icons";
import { isEmpty, isObject, isArray, isNumber } from "lodash";

/**
 * Recursive row component that renders expandable/collapsible status rows.
 * Supports unlimited nesting depth with indentation.
 *
 * @param {{ item: object, columns: { key: string, label: string }[], level: number }} props
 */
const StatusRow = ({ item, columns = [], level = 0 }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!isObject(item)) return null;

  const hasChildren = isArray(item.children) && !isEmpty(item.children);
  const statusText = item.status || "Unknown";
  const safeLevel = isNumber(level) ? Math.min(Math.max(level, 0), 10) : 0;
  const paddingLeft = safeLevel * 24;

  const toggleExpand = e => {
    if (hasChildren) {
      e?.stopPropagation();
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <>
      <div
        className={`flex justify-between items-center py-3 text-sm border-b border-solid border-t-0 border-x-0 border-gray-200 last:border-b-0 ${
          hasChildren ? "cursor-pointer" : ""
        }`}
        onClick={toggleExpand}
      >
        <div className="flex items-center gap-2" style={{ paddingLeft: `${paddingLeft}px` }}>
          {hasChildren && (
            <span className="flex items-center justify-center w-4">
              <CaretRightOutlined
                className={`text-[#00000073] text-xs transition-transform duration-300 ${
                  isExpanded ? "rotate-90" : ""
                }`}
              />
            </span>
          )}
          {!hasChildren && safeLevel > 0 && <span className="w-4" />}
          <span className="text-black-88 text-sm font-normal">{statusText}</span>
        </div>
        <div className="flex">
          {columns.map(col => {
            const value = isNumber(item[col.key]) ? item[col.key] : 0;
            return (
              <span
                key={col.key}
                className="text-black-88 text-sm font-normal w-[7rem] text-center"
              >
                {value}
              </span>
            );
          })}
        </div>
      </div>

      {hasChildren && (
        <div
          className="overflow-hidden transition-all duration-300 ease-in-out"
          style={{
            maxHeight: isExpanded ? `${(item.children?.length || 0) * 200}px` : "0px",
            opacity: isExpanded ? 1 : 0,
          }}
        >
          {item.children.map((child, index) => (
            <StatusRow
              key={child?.statusId || child?.status || `child-${safeLevel}-${index}`}
              item={child}
              columns={columns}
              level={safeLevel + 1}
            />
          ))}
        </div>
      )}
    </>
  );
};

/**
 * Reusable expandable status list with configurable value columns.
 *
 * @param {{
 *   data: Array<{ status: string, children?: Array, [key: string]: any }>,
 *   columns: Array<{ key: string, label: string }>,
 *   loading?: boolean,
 *   emptyText?: string,
 *   statusLabel?: string,
 * }} props
 *
 * @example
 * <ExpandableStatusList
 *   data={[
 *     { status: "Write-Up", count: 12, children: [{ status: "Sub 1", count: 5 }] },
 *     { status: "Sold", count: 98 },
 *   ]}
 *   columns={[{ key: "count", label: "No. of Opportunity" }]}
 * />
 */
const ExpandableStatusList = ({
  data = [],
  columns = [],
  loading = false,
  emptyText = "No data available",
  statusLabel = "Status",
}) => {
  const safeData = isArray(data) ? data : [];

  if (loading) {
    return <Skeleton.Button active block style={{ height: "16rem", borderRadius: "8px" }} />;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between text-sm font-medium border-b border-solid border-t-0 border-x-0 border-gray-200 pb-2">
        <span className="text-black-88 text-sm font-semibold">{statusLabel}</span>
        <div className="flex">
          {columns.map(col => (
            <span
              key={col.key}
              className="text-black-88 text-sm font-semibold w-[7rem] text-center"
            >
              {col.label}
            </span>
          ))}
        </div>
      </div>

      {/* Rows */}
      {!isEmpty(safeData) ? (
        safeData.map((item, index) => (
          <StatusRow
            key={item?.statusId || item?.status || `status-${index}`}
            item={item}
            columns={columns}
            level={0}
          />
        ))
      ) : (
        <div className="text-center h-[10rem] flex items-center justify-center py-8 text-gray-500">
          <Empty description={emptyText} />
        </div>
      )}
    </div>
  );
};

export default ExpandableStatusList;
